import React, { useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, StyleSheet, Text, View } from 'react-native';

import type { ChatSummary } from '../api/types';
import { useLocale } from '../i18n/LocaleContext';
import { chatListPalette } from '../theme/chatListPalette';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { deliverShareToChat, type ShareDeliveryProgress } from '../share/deliverShare';
import type { IncomingShare } from '../share/incomingShare';
import { shareTargets } from '../share/shareTargets';
import { AppPressable } from './AppPressable';
import { AvatarBadge } from './AvatarBadge';
import { BottomSheet, type BottomSheetPalette } from './BottomSheet';
import { SavedChatIcon } from './SavedChatIcon';

type Props = {
  /** Что пришло из системного меню. `null` — листа нет. */
  share: IncomingShare | null;
  chats: ChatSummary[];
  palette: BottomSheetPalette;
  onClose: () => void;
  /** Отправлено: экран решает, открывать ли этот чат. */
  onDelivered: (chat: ChatSummary) => void;
};

/**
 * Выбор чата для содержимого из системного «Поделиться» (стори 8.7).
 *
 * <p>Лист, а не отдельный экран: человек пришёл из другого приложения с одним делом — выбрать,
 * кому это отправить, — и после отправки уходит обратно или в открывшийся чат. Экран в стопке
 * навигации пришлось бы ещё и закрывать.
 *
 * <p>Отправка идёт прямо отсюда ({@link deliverShareToChat}), а не «предзаполнением поля ввода»
 * в чате. Причина простая: человек уже выбрал и содержимое, и адресата — второе подтверждение
 * в чужом приложении ничего не уточняет, а лишний шаг между «поделиться» и «отправлено»
 * теряется чаще всего именно здесь.
 */
export function ShareIntentSheet({ share, chats, palette, onClose, onDelivered }: Props): React.ReactElement {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  const targets = useMemo(() => shareTargets(chats), [chats]);

  const [progress, setProgress] = useState<ShareDeliveryProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sending = progress !== null;

  const send = async (chat: ChatSummary) => {
    if (!share || sending) return;
    setError(null);
    setProgress({ sent: 0, total: share.files.length + (share.text ? 1 : 0) });
    try {
      await deliverShareToChat(chat.id, share, setProgress);
      setProgress(null);
      onDelivered(chat);
    } catch (e) {
      setProgress(null);
      setError(e instanceof Error && e.message ? e.message : t('shareIntentFailed'));
    }
  };

  const summary = useMemo(() => {
    if (!share) return '';
    const parts: string[] = [];
    if (share.files.length > 0) {
      parts.push(t('shareIntentPhotos').replace('{count}', String(share.files.length)));
    }
    if (share.text) parts.push(share.text);
    return parts.join(' · ');
  }, [share, t]);

  return (
    <BottomSheet
      visible={share !== null}
      onClose={sending ? () => {} : onClose}
      palette={palette}
      title={t('shareIntentTitle')}>
      {/* Что именно уйдёт. Из чужого приложения человек приходит «вслепую»: подтверждение того,
          что мы приняли, стоит одной строки. */}
      {summary ? (
        <Text style={[styles.summary, { color: pal.hint }]} numberOfLines={2}>
          {summary}
        </Text>
      ) : null}
      {share && share.skippedFiles > 0 ? (
        <Text style={[styles.summary, { color: pal.hint }]}>
          {t('shareIntentSkipped').replace('{count}', String(share.skippedFiles))}
        </Text>
      ) : null}
      {error ? <Text style={[styles.summary, { color: '#ef4444' }]}>{error}</Text> : null}

      {sending ? (
        <View style={styles.sendingRow}>
          <ActivityIndicator color={pal.modalPrimary} />
          <Text style={[styles.sendingText, { color: pal.dmRowName }]}>
            {t('shareIntentSending')
              .replace('{sent}', String(progress?.sent ?? 0))
              .replace('{total}', String(progress?.total ?? 0))}
          </Text>
        </View>
      ) : null}

      <FlatList
        style={styles.list}
        data={targets}
        keyExtractor={(item) => item.id}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={<Text style={[styles.empty, { color: pal.dmEmpty }]}>{t('shareIntentEmpty')}</Text>}
        renderItem={({ item }) => (
          <AppPressable
            style={[styles.row, { borderBottomColor: pal.channelDirRowBorder }]}
            disabled={sending}
            onPress={() => void send(item)}
            accessibilityRole="button"
            accessibilityLabel={item.type === 'SAVED' ? t('savedChatTitle') : item.title}>
            {item.type === 'SAVED' ? (
              <SavedChatIcon size={40} />
            ) : item.type === 'DIRECT' ? (
              <AvatarBadge
                userId={item.peerUserId ?? undefined}
                displayName={item.title}
                hasAvatar={item.peerHasAvatar}
                revision={item.peerAvatarRevision ?? 0}
                shapeId={item.peerAvatarShape}
                size={40}
              />
            ) : (
              <AvatarBadge
                chatAvatarChatId={item.id}
                displayName={item.title}
                hasAvatar={item.chatHasAvatar}
                revision={item.chatAvatarRevision ?? 0}
                shapeId={item.chatAvatarShape}
                size={40}
              />
            )}
            <View style={styles.rowText}>
              <Text style={[styles.rowName, { color: pal.dmRowName }]} numberOfLines={1}>
                {item.type === 'SAVED' ? t('savedChatTitle') : item.title}
              </Text>
              {item.type === 'SAVED' ? (
                <Text style={[styles.rowHint, { color: pal.hint }]} numberOfLines={1}>
                  {t('shareIntentSavedHint')}
                </Text>
              ) : null}
            </View>
          </AppPressable>
        )}
      />
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  summary: { fontSize: 13, marginBottom: 6 },
  sendingRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  sendingText: { fontSize: 13 },
  list: { flexGrow: 0, flexShrink: 1 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  rowText: { flex: 1, minWidth: 0 },
  rowName: { fontSize: 16, fontWeight: '600' },
  rowHint: { fontSize: 12, marginTop: 2 },
  empty: { fontSize: 14, textAlign: 'center', paddingVertical: 24 },
});
